import * as React from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectProps } from '@mui/material/Select';

/**
 * A single option within a {@link MSelectGroup}.
 */
interface MSelectGroupOption {
    /** Value submitted when this option is selected. */
    id: string | number;
    /** Display label rendered for the option. */
    name: string;
}

/**
 * A labelled group of options rendered as a native `<optgroup>`.
 */
interface MSelectGroup {
    /** `<optgroup>` label. */
    label: string;
    /** Options belonging to this group. */
    options: MSelectGroupOption[];
}

/**
 * Props for {@link MSelectGrouping}.
 */
interface MSelectGroupingProps extends Omit<SelectProps, 'defaultValue' | 'onChange'> {
    /** Field label rendered above the select. */
    label?: string;
    /** Grouped options. */
    choices: MSelectGroup[];
    /** Pre-selected value applied on first render and reapplied whenever it changes. */
    defaultValue?: string | number;
    /**
     * Called with the newly selected value.
     *
     * @param value - The selected option id.
     */
    onChange: (value: string | number) => void;
    /** Renders a leading empty `<option>`. Defaults to `true`. */
    isEmpty?: boolean;
}

/**
 * A MUI {@link Select} configured as a native `<select>` with `<optgroup>`
 * sections, for cases where grouped options are needed with native rendering.
 */
const MSelectGrouping: React.FC<MSelectGroupingProps> = ({ label, choices, defaultValue, onChange, isEmpty = true, ...props }) => {
    const [value, setValue] = React.useState<string | number>(defaultValue ?? '');

    React.useEffect(() => {
        if (defaultValue !== undefined) setValue(defaultValue);
    }, [defaultValue]);

    const handleChange: SelectProps['onChange'] = (e) => {
        const { value } = e.target;
        setValue(value as string | number);
        onChange(value as string | number);
    };

    return (
        <FormControl sx={{ minWidth: '100%' }}>
            <InputLabel id="demo-select-small">{label}</InputLabel>
            <Select
                {...props}
                labelId="demo-select-small"
                id="group-native-select" native
                value={value}
                label={label}
                onChange={handleChange as any}
            >
                {isEmpty ? <option aria-label="None" value="" /> : null}
                {choices.map((group, groupIndex) => (
                    <optgroup key={groupIndex} label={group.label}>
                        {group.options.map((option, index) => (
                            <option key={index} value={option.id} >
                                {option.name}
                            </option>
                        ))}
                    </optgroup>
                ))}
            </Select>
        </FormControl>
    );
};

export default React.memo(MSelectGrouping);
